import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, HeartHandshake } from "lucide-react";
import { scrollToId, IMAGES } from "./data";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero({ onEnquire }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  const lines = [
    <span key="l1">Skin that tells</span>,
    <span key="l2">your story —</span>,
    <em key="l3" className="italic font-light text-clay">
      honestly.
    </em>,
  ];

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-40 w-[42rem] h-[42rem] rounded-full bg-clay/15 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 -left-48 w-[36rem] h-[36rem] rounded-full bg-gold/15 blur-3xl"
      />

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-32 lg:pt-40 pb-24 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.img
            src="/logo-emblem.png"
            alt="Luviae Aesthetics emblem"
            initial={{ opacity: 0, scale: 0.85, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.05, ease: EASE }}
            className="w-16 h-16 rounded-full border border-[#E5DDD0] shadow-[0_16px_32px_-16px_rgba(44,38,35,0.35)] mb-8"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="text-xs uppercase tracking-[0.25em] text-clay font-semibold mb-8"
          >
            Luviae Aesthetics · Mississauga
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light tracking-tight leading-[1.05] text-ink"
          >
            {lines.map((line, i) => (
              <span key={i} className="mask-line pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-md text-base sm:text-lg text-ink/70 leading-relaxed"
          >
            Laser, microneedling and advanced skin treatments in a calm,
            unhurried sanctuary. No pressure, no packages you don't need —
            just honest skin.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToId("treatments")}
              className="inline-flex items-center gap-2 rounded-full bg-ink text-cream text-sm font-semibold px-7 py-3.5 hover:bg-clay hover:-translate-y-0.5 transition-all duration-300"
            >
              Explore 7 treatments
              <ArrowDown size={16} />
            </button>
            <button
              data-testid="hero-cta-enquire"
              onClick={onEnquire}
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 text-ink text-sm font-semibold px-7 py-3.5 hover:border-clay hover:text-clay hover:-translate-y-0.5 transition-all duration-300"
            >
              Enquire now
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: imgY }}
            className="relative rounded-t-[999px] rounded-b-[2rem] overflow-hidden aspect-[3/4] max-w-sm mx-auto border border-[#E5DDD0] shadow-[0_40px_80px_-30px_rgba(44,38,35,0.35)]"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Natural glowing skin after treatment at Luviae Aesthetics"
              className="w-full h-full object-cover"
              initial={{ scale: 1.25 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.8, delay: 0.3, ease: EASE }}
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-t from-clay/25 via-transparent to-gold/10 mix-blend-multiply"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
            className="absolute top-8 -left-2 sm:left-4"
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="flex items-center gap-2 bg-cream/90 backdrop-blur-md border border-[#E5DDD0] rounded-full px-4 py-2.5 shadow-lg"
            >
              <ShieldCheck size={16} className="text-sage" />
              <span className="text-xs font-semibold text-ink">Qualified &amp; insured</span>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4, ease: EASE }}
            className="absolute bottom-10 -right-2 sm:right-2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="flex items-center gap-2 bg-cream/90 backdrop-blur-md border border-[#E5DDD0] rounded-full px-4 py-2.5 shadow-lg"
            >
              <HeartHandshake size={16} className="text-clay" />
              <span className="text-xs font-semibold text-ink">No-pressure consultations</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
